// Constants and Event Types
if (!customElements.get('product-info')) {
  const EVENTS = {
    PRODUCT_INFO_LOADED: 'product-info:loaded',
    VARIANT_CHANGE: PUB_SUB_EVENTS.variantChange,
    OPTION_VALUE_CHANGE: PUB_SUB_EVENTS.optionValueSelectionChange,
    CART_UPDATE: PUB_SUB_EVENTS.cartUpdate,
    QUANTITY_UPDATE: PUB_SUB_EVENTS.quantityUpdate,
  };

  // Utility functions
  const Utilities = {
    HTMLUpdate: {
      viewTransition(target, source, preCallbacks = [], postCallbacks = []) {
        preCallbacks.forEach((callback) => callback(source));
        target.innerHTML = source.innerHTML;
        postCallbacks.forEach((callback) => callback(target));
      },
    },

    PriceFormat: {
      format(price) {
        if (Shopify.currency.active === 'SEK') {
          return (
            (price / 100).toLocaleString('sv-SE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }) + ' kr'
          );
        }
        return (price / 100).toLocaleString(undefined, {
          style: 'currency',
          currency: Shopify.currency.active,
        });
      },
    },
  };

  // Cart API Service
  class CartService {
    static async addItem(variantId, quantity = 1) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 422) {
          throw new Error('Produkten är slutsåld');
        }
        throw new Error(errorData.description || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    }

    static async removeItem(lineNumber) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line: lineNumber,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    }

    static async getCart() {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      return response.json();
    }
  }

  // Recommended Skins Component
  class RecommendedSkins {
    constructor(container, options = {}) {
      this.container = container;
      this.options = options;
      this.isProcessing = false;
    }

    async update(variantId) {
      console.log('Updating recommended skins for variant:', variantId);
      if (!this.container || !variantId) {
        console.log('Container or variant not found');
        return;
      }

      const variant = window.variantData?.find((v) => v.id === parseInt(variantId, 10));
      if (!variant) return;

      // Check if this is a splitboard product
      if (variant.productType !== 'Splitboard') {
        this._hideContainer();
        return;
      }

      try {
        const cartData = await CartService.getCart();
        const validSkins = this._getValidSkins(variant);

        if (!validSkins.length) {
          this._hideContainer();
          return;
        }

        this._renderSkins(validSkins, cartData, variant);
        this._setupEventListeners();
      } catch (error) {
        console.error('Error updating recommended skins:', error);
        this._hideContainer();
      }
    }

    _getValidSkins(variant) {
      let recommendedSkinSizes = variant.recommendedSkinSizes;
      if (typeof recommendedSkinSizes === 'string') {
        try {
          recommendedSkinSizes = JSON.parse(recommendedSkinSizes);
        } catch (e) {
          console.error('Failed to parse recommendedSkinSizes JSON:', e);
          return [];
        }
      }

      return recommendedSkinSizes.filter((skin) => skin && skin.id && skin.available === true && skin.name);
    }

    _renderSkins(skins, cartData, variant) {
      const boardLength = variant.title.split(' ')[0];
      const productTitle = document.querySelector('.product__title h1')?.textContent?.trim() || '';
      const chooseSkinText = window.theme?.translations?.skins?.choose_skins_for || 'Choose skins for:';
      let html = `<h2 class="skin-sizes-title">${chooseSkinText}<br><span class="skin-sizes-product-name">${productTitle} ${boardLength}</span></h2><div class="skin-sizes-list">`;

      skins.forEach((skin) => {
        const isInCart = cartData.items.some((item) => item.variant_id === parseInt(skin.id, 10));
        const formattedPrice = Utilities.PriceFormat.format(skin.price);

        html += this._getSkinItemHTML(skin, isInCart, formattedPrice);
      });

      html += '</div>';
      this.container.innerHTML = html;
      this.container.style.display = 'block';
    }

    _getSkinItemHTML(skin, isInCart, formattedPrice) {
      return `
        <div class="skin-size-item" data-variant-id="${skin.id}">
          <label class="skin-checkbox-container">
            <input type="checkbox" class="skin-checkbox" data-variant-id="${skin.id}" ${isInCart ? 'checked' : ''}>
            <span class="checkmark">X</span>
            <div class="skin-info">
              <span class="skin-name">${skin.name || skin.public_title}</span>
              <span class="skin-price">${formattedPrice}</span>
            </div>
          </label>
        </div>`;
    }

    _hideContainer() {
      this.container.innerHTML = '';
      this.container.style.display = 'none';
    }

    async _handleSkinCheckboxChange(checkbox) {
      if (this.isProcessing) return;
      this.isProcessing = true;

      const container = checkbox.closest('.skin-checkbox-container');
      const skinItem = checkbox.closest('.skin-size-item');
      const skinVariantId = parseInt(skinItem?.dataset.variantId, 10);

      try {
        if (!skinVariantId) {
          throw new Error('Could not determine which skin to update');
        }

        container.classList.add('flash');

        const cartData = await CartService.getCart();
        const itemInCart = cartData.items.some((item) => item.variant_id === skinVariantId);

        if (checkbox.checked === itemInCart) {
          return;
        }

        if (checkbox.checked) {
          const response = await CartService.addItem(skinVariantId);
          this._updateCartUI();
        } else {
          const lineItem = cartData.items.find((item) => item.variant_id === skinVariantId);
          if (lineItem) {
            const lineNumber = cartData.items.indexOf(lineItem) + 1;
            await CartService.removeItem(lineNumber);
            this._updateCartUI();
          }
        }

        await this._updateCheckboxState(skinVariantId);
      } catch (error) {
        console.error('Error updating cart:', error);
        checkbox.checked = !checkbox.checked;
        alert(
          error.message === 'Produkten är slutsåld'
            ? 'Tyvärr är denna produkt slutsåld.'
            : 'Det gick inte att uppdatera varukorgen. Försök igen.'
        );
      } finally {
        this.isProcessing = false;
        setTimeout(() => {
          container.classList.remove('flash');
        }, 400);
      }
    }

    _updateCartUI() {
      const cartNotification = document.querySelector('cart-notification');
      const cartIconBubble = document.getElementById('cart-icon-bubble');

      // Show cart notification if available
      if (cartNotification?.show) cartNotification.show();

      // Update cart count
      if (cartIconBubble) {
        CartService.getCart().then((cart) => {
          const totalItems = cart.item_count;

          // Get or create the cart count bubble
          let cartBubble = cartIconBubble.querySelector('.cart-count-bubble');
          const svgWrapper = cartIconBubble.querySelector('.svg-wrapper');

          if (totalItems > 0) {
            // Update or create cart bubble for items
            if (!cartBubble) {
              cartBubble = document.createElement('div');
              cartBubble.className = 'cart-count-bubble';
              cartBubble.innerHTML = `
                <span aria-hidden="true">${totalItems}</span>
                <span class="visually-hidden">${totalItems} artiklar</span>
              `;
              cartIconBubble.appendChild(cartBubble);
            } else {
              const spans = cartBubble.querySelectorAll('span');
              spans.forEach((span) => {
                if (!span.classList.contains('visually-hidden')) {
                  span.textContent = totalItems;
                } else {
                  span.textContent = `${totalItems} artiklar`;
                }
              });
            }

            // Update cart icon to filled version
            const cartIcon = svgWrapper.querySelector('svg');
            if (cartIcon.classList.contains('icon-cart-empty')) {
              cartIcon.classList.remove('icon-cart-empty');
              cartIcon.classList.add('icon-cart');
              cartIcon.innerHTML = `<path fill="currentColor" fill-rule="evenodd" d="M20.5 6.5a4.75 4.75 0 0 0-4.75 4.75v.56h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33l-.77-11.6h-3.16v-.57A4.75 4.75 0 0 0 20.5 6.5m3.75 5.31v-.56a3.75 3.75 0 1 0-7.5 0v.56zm-7.5 1h7.5v.56a3.75 3.75 0 1 1-7.5 0zm-1 0v.56a4.75 4.75 0 1 0 9.5 0v-.56h2.22l.71 10.67a4 4 0 0 1-3.99 4.27h-7.38a4 4 0 0 1-4-4.27l.72-10.67z"></path>`;
            }
          } else {
            // Remove cart bubble if cart is empty
            if (cartBubble) {
              cartBubble.remove();
            }

            // Update cart icon to empty version
            const cartIcon = svgWrapper.querySelector('svg');
            if (!cartIcon.classList.contains('icon-cart-empty')) {
              cartIcon.classList.remove('icon-cart');
              cartIcon.classList.add('icon-cart-empty');
              cartIcon.innerHTML = `<path fill="currentColor" fill-rule="evenodd" d="M15.75 11.8h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33L28.4 11.8zm0 1h-2.22l-.71 10.67a4 4 0 0 0 3.99 4.27h7.38a4 4 0 0 0 4-4.27l-.72-10.67h-2.22v.63a4.75 4.75 0 1 1-9.5 0zm8.5 0h-7.5v.63a3.75 3.75 0 1 0 7.5 0z"></path>`;
            }
          }
        });
      }
    }

    async _updateCheckboxState(variantId) {
      try {
        const cart = await CartService.getCart();
        const checkbox = this.container.querySelector(`.skin-size-item[data-variant-id="${variantId}"] .skin-checkbox`);

        if (checkbox) {
          const inCart = cart.items.some((item) => item.variant_id === parseInt(variantId, 10) && item.quantity > 0);
          checkbox.checked = inCart;
        }
      } catch (error) {
        console.error('Error updating checkbox state:', error);
      }
    }

    _setupEventListeners() {
      if (this.container.hasEventListener) return;

      this.container.addEventListener('change', async (e) => {
        if (!e.target.matches('.skin-checkbox')) return;
        await this._handleSkinCheckboxChange(e.target);
      });

      this.container.hasEventListener = true;
    }
  }

  // Main ProductInfo Component
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.quantityInput = undefined;
        this.quantityForm = undefined;
        this.onVariantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.abortController = undefined;
        this.pendingRequestUrl = null;
        this.preProcessHtmlCallbacks = [];
        this.postProcessHtmlCallbacks = [];
        this.recommendedSkins = null;
        this.enrichedVariants = null; // Add local storage for enriched variants

        // Added properties for custom variant info
        this.variantInfoContainer = null;
        this.shortInfoContainer = null; // Add container for short info
        this.infoModal = null;
        this.infoModalTitle = null;
        this.infoModalBody = null;
        this.infoModalCloseButton = null;
        this.translations = window.translations || {};
      }

      // Add method to fix quick-add modal
      addPreProcessCallback(callback) {
        if (typeof callback === 'function') {
          this.preProcessHtmlCallbacks.push(callback);
        }
      }

      connectedCallback() {
        this.quantityInput = this.querySelector('.quantity__input');
        this.recommendedSkins = new RecommendedSkins(document.getElementById('recommended-skins-container'));

        // Find the custom variant info container *within this component*
        this.variantInfoContainer = this.querySelector('.custom-variant-info-container');

        // Find or create the short info container
        this.shortInfoContainer = this.querySelector('.custom-short-info-container');
        if (!this.shortInfoContainer) {
          this.shortInfoContainer = document.createElement('div');
          this.shortInfoContainer.classList.add('custom-short-info-container');
          this.shortInfoContainer.style.display = 'none';

          // Find the buy buttons container to insert after
          const buyButtonsContainer = this.querySelector('.product-form');
          if (buyButtonsContainer) {
            buyButtonsContainer.after(this.shortInfoContainer);
          }
        }

        // Find the global info modal elements
        this.infoModal = document.getElementById('inla-info-modal');
        this.infoModalTitle = document.getElementById('inla-modal-title');
        this.infoModalBody = document.getElementById('inla-modal-body');
        this.infoModalCloseButton = document.getElementById('inla-close-modal');

        // Setup modal listeners once
        this._setupInfoModalListeners();

        this._initializeComponents();
        this._setupEventListeners();
        this._updateInitialState();

        // Use requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          // Update short info container with product metafield data
          this._updateShortInfoContainer();

          // Get initial variant data from JSON
          const variantJson = this.querySelector('[data-selected-variant]');
          if (variantJson) {
            try {
              // Parse the variant data from the JSON
              const variantData = JSON.parse(variantJson.textContent);
              console.log('Initial variant data from JSON:', variantData);

              // Store enriched variants locally
              if (window.variantData && Array.isArray(window.variantData)) {
                this.enrichedVariants = window.variantData;
                console.log('Stored enriched variants locally:', this.enrichedVariants);
              }

              // Try to find full variant data in local storage first
              let variantToDisplay = variantData;
              if (this.enrichedVariants) {
                const foundVariant = this.enrichedVariants.find((v) => v.id === parseInt(variantData.id, 10));
                if (foundVariant) {
                  console.log('Found variant in local enriched variants, using enriched data');
                  variantToDisplay = foundVariant;
                } else {
                  console.log('Variant not found in local enriched variants, using JSON data directly');
                }
              }

              // Update the custom variant info with the data we have
              this._updateCustomVariantInfo(variantToDisplay);

              // For single-variant products, ensure the variant info is displayed
              const variantSelects = this.querySelector('variant-selects');
              if (variantSelects) {
                const options = variantSelects.querySelectorAll('input[type="radio"]');
                if (options.length === 1) {
                  console.log('Single variant product detected, ensuring variant info is displayed');
                  // Force update the variant info
                  this._updateCustomVariantInfo(variantToDisplay);
                }
              }
            } catch (e) {
              console.error('Error parsing variant JSON:', e);
              this._updateCustomVariantInfo(null);
            }
          } else {
            console.warn('No variant JSON data found');
            this._updateCustomVariantInfo(null);
          }
        });

        this.dispatchEvent(new CustomEvent(EVENTS.PRODUCT_INFO_LOADED, { bubbles: true }));
      }

      disconnectedCallback() {
        // Clean up any modal listeners when the component is disconnected
        this._cleanupModalListeners();

        // Clean up other event listeners
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      _initializeComponents() {
        this.initializeProductSwapUtility();
        this.onVariantChangeUnsubscriber = subscribe(
          EVENTS.OPTION_VALUE_CHANGE,
          this.handleOptionValueChange.bind(this)
        );
        this.initQuantityHandlers();

        // Update recommended skins for the initial variant
        const initialVariantElem = this.querySelector('variant-selects [data-selected-variant]');
        if (initialVariantElem) {
          const variant = JSON.parse(initialVariantElem.innerHTML);
          this.recommendedSkins.update(variant.id);
        }

        // Initialize any legacy skin add buttons if they exist
        this.initializeSkinAddButtons();
      }

      _setupEventListeners() {
        // Add event listener for the main product form submission
        this.productForm?.querySelector('form')?.addEventListener('submit', async (e) => {
          e.preventDefault();

          try {
            // Get the main product variant ID and quantity from form
            const mainProductForm = e.target;
            const formData = new FormData(mainProductForm);
            const mainVariantId = formData.get('id');
            const mainQuantity = parseInt(formData.get('quantity') || 1, 10);

            // Add the main product
            const addResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                items: [
                  {
                    id: mainVariantId,
                    quantity: mainQuantity,
                  },
                ],
              }),
            });

            if (!addResponse.ok) {
              const errorData = await addResponse.json();
              throw new Error(errorData.description || 'Error updating cart');
            }

            const responseData = await addResponse.json();

            // Update cart count
            document.getElementById('cart-icon-bubble')?.querySelector('cart-count')?.updateCount();

            // Show cart notification
            const cartNotification = document.querySelector('cart-notification');
            if (cartNotification) {
              cartNotification.setActiveElement(document.activeElement);
              cartNotification.show(responseData);
            }
          } catch (error) {
            console.error('Error processing cart update:', error);
            const errorMessage = document.querySelector('.product-form__error-message');
            if (errorMessage) {
              errorMessage.textContent = error.message;
              errorMessage.style.display = 'block';
            }
          }
        });
      }

      _updateInitialState() {
        // Implementation of _updateInitialState method
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        if (productForm) {
          productForm.toggleSubmitButton(true);
          productForm.handleErrorMessage();
        }
      }

      async renderProductInfo({ requestUrl, targetId, callback }) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        try {
          const response = await fetch(requestUrl, { signal: this.abortController.signal });
          const responseText = await response.text();
          this.pendingRequestUrl = null;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          await callback(html);

          document.querySelector(`#${targetId}`)?.focus();
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error('Error rendering product info:', error);
          }
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          if (this.productModal) this.productModal.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            Utilities.HTMLUpdate.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            Utilities.HTMLUpdate.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          if (this.pickupAvailability) this.pickupAvailability.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this._updateCustomVariantInfo(null);
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          this.updateQuantityRules(this.sectionId, html);
          if (this.productForm) {
            this.productForm.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );
          }

          // Update the Recommended Skin Sizes section
          this.recommendedSkins.update(variant.id);

          // Update short info container
          this._updateShortInfoContainer();

          // Try to find enriched variant data in local storage first
          let enrichedVariant = variant;
          if (this.enrichedVariants) {
            const foundVariant = this.enrichedVariants.find((v) => v.id === parseInt(variant.id, 10));
            if (foundVariant) {
              console.log('Found enriched variant data in local storage for variant change');
              enrichedVariant = foundVariant;
            }
          }

          // Fallback to window.variantData if not found locally
          if (!enrichedVariant && window.variantData && Array.isArray(window.variantData)) {
            const foundVariant = window.variantData.find((v) => v.id === parseInt(variant.id, 10));
            if (foundVariant) {
              console.log('Found enriched variant data in window.variantData for variant change (fallback)');
              enrichedVariant = foundVariant;
            }
          }

          // Update the custom variant info with the enriched data
          this._updateCustomVariantInfo(enrichedVariant);

          publish(EVENTS.VARIANT_CHANGE, {
            data: {
              sectionId: this.sectionId,
              html,
              variant: enrichedVariant,
            },
          });
        };
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        if (!selectedVariant) return null;

        try {
          const variantData = JSON.parse(selectedVariant);
          console.log('Selected variant data:', variantData);

          // Try to find enriched variant data in local storage first
          let enrichedVariant = null;
          if (this.enrichedVariants) {
            enrichedVariant = this.enrichedVariants.find((v) => v.id === parseInt(variantData.id, 10));
            if (enrichedVariant) {
              console.log('Found enriched variant data in local storage for getSelectedVariant');
              return enrichedVariant;
            }
          }

          // Fallback to window.variantData if not found locally
          if (window.variantData && Array.isArray(window.variantData)) {
            enrichedVariant = window.variantData.find((v) => v.id === parseInt(variantData.id, 10));
            if (enrichedVariant) {
              console.log('Found enriched variant data in window.variantData for getSelectedVariant (fallback)');
              return enrichedVariant;
            }
          }

          return variantData;
        } catch (e) {
          console.error('Error parsing selected variant data:', e);
          return null;
        }
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          Utilities.HTMLUpdate.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        if (!shouldFetchFullPage) {
          params.push(`section_id=${this.sectionId}`);
        }
        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }
        return `${url}?${params.join('&')}`;
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );
        if (this.dataset.updateUrl !== 'false') {
          window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId || '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      setUnavailable() {
        if (this.productForm) {
          this.productForm.toggleSubmitButton(true, window.variantStrings.unavailable);
        }
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector('media-gallery ul');
        this.querySelector('media-gallery')?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );
        const modalContent = this.productModal?.querySelector('.product-media-modal__content');
        const newModalContent = html.querySelector('product-modal .product-media-modal__content');
        if (modalContent && newModalContent) {
          modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      setQuantityBoundaries() {
        if (!this.quantityInput) return;
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };
        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        this.quantityInput.min = min;
        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;
        publish(EVENTS.QUANTITY_UPDATE, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;
        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundaries();
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      // Getters for productForm, productModal, pickupAvailability, and variantSelectors.
      get productForm() {
        return this.querySelector('product-form');
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector('pickup-availability');
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      // Legacy skin add button handler
      initializeSkinAddButtons() {
        this.querySelectorAll('.skin-add-button').forEach((button) => {
          button.addEventListener('click', async (event) => {
            event.preventDefault();
            const skinItem = button.closest('.recommended-skin-item');
            const variantId = skinItem.getAttribute('data-variant-id');

            button.disabled = true;
            button.textContent = 'Adding...';

            try {
              const data = await CartService.addItem(parseInt(variantId, 10));
              button.textContent = 'Added to cart';
              button.classList.add('disabled');
              document.getElementById('cart-icon-bubble').querySelector('cart-count')?.updateCount();
              if (window.CartNotification && typeof window.CartNotification.show === 'function') {
                window.CartNotification.show(data);
              }
            } catch (error) {
              console.error('Error adding item:', error);
              button.disabled = false;
              button.textContent = 'Add to cart';
              alert('There was an error adding the item to your cart. Please try again.');
            }
          });
        });
      }

      // Initialization Methods
      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundaries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(EVENTS.CART_UPDATE, this.fetchQuantityRules.bind(this));
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => {
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel'));
        });
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      _updateCustomVariantInfo(selectedVariant) {
        if (!this.variantInfoContainer) {
          return;
        }
        if (!selectedVariant) {
          this.variantInfoContainer.innerHTML = '';
          this.variantInfoContainer.style.display = 'none';
          return;
        }

        const variant = selectedVariant;
        let infoHtml = '';
        let productType = null;

        // Initialize empty infoHtml
        infoHtml = '';

        // --- Logic to build infoHtml matching the original format ---
        // Snowboards - Modified condition to be more flexible
        if (variant.riderLengthMin && variant.riderLengthMax) {
          const headerText =
            Shopify.locale === 'sv' || document.documentElement.lang === 'sv' ? 'Tips på storlek:' : 'Size guide:';
          infoHtml = `<div class="variant-info-header">${headerText}</div>`;

          infoHtml += '<div class="variant-info-content">';
          const riderLengthRangeText = variant.riderLengthMin + '-' + variant.riderLengthMax;
          const bootSize = variant.maxBootSize
            ? variant.maxBootSize % 1 === 0.5
              ? variant.maxBootSize.toFixed(1)
              : Math.floor(variant.maxBootSize)
            : '';

          infoHtml +=
            '<strong>' +
            variant.title +
            '</strong> ' +
            translations.suitableFor +
            ' <strong>' +
            riderLengthRangeText +
            ' cm</strong><br>';

          // Check if both min and max boot sizes exist for bundles
          if (variant.minBootSize && variant.maxBootSize) {
            const minBoot =
              variant.minBootSize % 1 === 0.5 ? variant.minBootSize.toFixed(1) : Math.floor(variant.minBootSize);
            const maxBoot =
              variant.maxBootSize % 1 === 0.5 ? variant.maxBootSize.toFixed(1) : Math.floor(variant.maxBootSize);
            infoHtml += translations.bootsBetween + ' <strong>' + minBoot + '-' + maxBoot + '</strong><br>';
          } else if (bootSize) {
            infoHtml += translations.bootsUpTo + ' <strong>' + bootSize + '</strong><br>';
          }

          if (variant.boardRadius) {
            infoHtml += translations.boardRadiusLabel + ': <strong>' + variant.boardRadius + ' m</strong><br>';
          }
          if (variant.boardWaistWidth) {
            infoHtml += translations.boardWaistWidthLabel + ': <strong>' + variant.boardWaistWidth + ' cm</strong><br>';
          }
          if (variant.optimalSkinSize) {
            infoHtml += translations.optimalSkinSizeLabel + ' <strong>' + variant.optimalSkinSize + '</strong><br>';
          }
          infoHtml += '</div>';
          productType = 'snowboards';
        }
        // Bindningar (bindings) - Modified condition to be more flexible
        else if (variant.sizeGuideForBindings) {
          const headerText =
            Shopify.locale === 'sv' || document.documentElement.lang === 'sv' ? 'Tips på storlek:' : 'Size guide:';
          infoHtml = `<div class="variant-info-header">${headerText}</div>`;

          infoHtml += '<div class="variant-info-content">';
          if (variant.sizeGuideForBindings.size) {
            infoHtml += translations.sizeBinding + ' <strong>' + variant.sizeGuideForBindings.size + '</strong> ';
          }
          if (variant.sizeGuideForBindings.sizeOutput) {
            infoHtml +=
              translations.fitsWellFor + ' <strong><br>' + variant.sizeGuideForBindings.sizeOutput + '</strong><br>';
          }
          infoHtml += '</div>';
          productType = 'bindings';
        }
        // Boots - Modified condition to be more flexible
        else if (variant.sizeConversion) {
          const headerText =
            Shopify.locale === 'sv' || document.documentElement.lang === 'sv' ? 'Tips på storlek:' : 'Size guide:';
          infoHtml = `<div class="variant-info-header">${headerText}</div>`;

          infoHtml += '<div class="variant-info-content">';
          infoHtml += translations.sizeChartSelected + '<br>';

          if (variant.sizeConversion.usSize || variant.sizeConversion.euroSize || variant.sizeConversion.cmSize) {
            infoHtml += '<strong>';
            if (variant.sizeConversion.usSize) infoHtml += variant.sizeConversion.usSize + ' / ';
            if (variant.sizeConversion.euroSize) infoHtml += variant.sizeConversion.euroSize + ' / ';
            if (variant.sizeConversion.cmSize) infoHtml += 'MONDO ' + variant.sizeConversion.cmSize + ' cm';
            infoHtml += '</strong>';
          }

          infoHtml += '</div>';
          productType = 'boots';
        }

        this.variantInfoContainer.innerHTML = infoHtml;
        this.variantInfoContainer.style.display = infoHtml ? 'block' : 'none';

        // Remove previous listener before adding new one
        const existingIcon = this.variantInfoContainer.querySelector('.inla-info-icon');
        if (existingIcon) {
          const clone = existingIcon.cloneNode(true);
          existingIcon.parentNode.replaceChild(clone, existingIcon); // Simple way to remove listeners
        }

        if (infoHtml && productType) {
          const infoIcon = document.createElement('span');
          infoIcon.classList.add('inla-info-icon');
          infoIcon.textContent = 'i'; // Match original format
          infoIcon.setAttribute('role', 'button');
          infoIcon.setAttribute(
            'aria-label',
            translations?.productInfo?.more_info_button_label || 'Visa mer information'
          );
          infoIcon.setAttribute('tabindex', '0'); // Make it focusable

          // Add listener correctly scoped to the class instance
          infoIcon.addEventListener('click', () => this._openInfoModal(productType, this.translations?.productInfo));
          infoIcon.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this._openInfoModal(productType, this.translations?.productInfo);
            }
          });
          this.variantInfoContainer.appendChild(infoIcon);
        }
      }

      _setupInfoModalListeners() {
        if (!this.infoModal) return;

        // Clean up existing listeners first
        this._cleanupModalListeners();

        // Store listener references for cleanup
        this._closeButtonListener = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._closeInfoModal();
        };

        this._backdropListener = (e) => {
          if (e.target === this.infoModal) {
            e.preventDefault();
            e.stopPropagation();
            this._closeInfoModal();
          }
        };

        // Add new listeners
        this.infoModalCloseButton?.addEventListener('click', this._closeButtonListener);
        this.infoModal?.addEventListener('click', this._backdropListener);
      }

      _cleanupModalListeners() {
        if (this.infoModalCloseButton && this._closeButtonListener) {
          this.infoModalCloseButton.removeEventListener('click', this._closeButtonListener);
          this._closeButtonListener = null;
        }

        if (this.infoModal && this._backdropListener) {
          this.infoModal.removeEventListener('click', this._backdropListener);
          this._backdropListener = null;
        }
      }

      _closeInfoModal() {
        if (!this.infoModal) return;

        // First remove the show class to trigger animation
        this.infoModal.classList.remove('show');

        // Wait for animation to complete
        const handleTransitionEnd = () => {
          // Reset modal state
          this.infoModal.style.visibility = 'hidden';
          this.infoModal.style.pointerEvents = 'none';
          this.infoModal.style.zIndex = '';

          // Clean up the transition listener
          this.infoModal.removeEventListener('transitionend', handleTransitionEnd);
        };

        // Add transition listener
        this.infoModal.addEventListener('transitionend', handleTransitionEnd);

        // Fallback in case transition doesn't fire
        setTimeout(() => {
          if (this.infoModal.style.visibility !== 'hidden') {
            handleTransitionEnd();
          }
        }, 300); // 300ms is a common transition duration
      }

      _openInfoModal(productType, productInfo) {
        if (!this.infoModal || !this.infoModalTitle || !this.infoModalBody || !productInfo) return;

        // Reset modal state before opening
        this.infoModal.style.visibility = 'hidden';
        this.infoModal.style.pointerEvents = 'none';
        this.infoModal.classList.remove('show');

        // Clean up any existing listeners and set up new ones
        this._setupInfoModalListeners();

        // Update modal content
        this.infoModalTitle.textContent = productInfo[productType]?.title || 'Information';
        let contentHtml = '';

        if (Array.isArray(productInfo[productType]?.points)) {
          contentHtml = productInfo[productType].points.map((point) => `<p>${point}</p>`).join('');
        } else if (typeof productInfo[productType]?.content === 'string') {
          contentHtml = productInfo[productType].content;
        }

        this.infoModalBody.innerHTML = contentHtml;

        // Make modal visible but still transparent
        this.infoModal.style.visibility = 'visible';
        this.infoModal.style.pointerEvents = 'auto';
        this.infoModal.style.zIndex = '9999';

        // Force reflow and add show class for animation
        void this.infoModal.offsetWidth;
        this.infoModal.classList.add('show');
      }

      // Add method to update short info container
      _updateShortInfoContainer() {
        if (!this.shortInfoContainer) return;

        // Check if we have short info in the window object (populated from Liquid)
        const shortInfo = window.productShortInfo || '';

        if (shortInfo && shortInfo.trim() !== '') {
          // Create header
          const headerText =
            Shopify.locale === 'sv' || document.documentElement.lang === 'sv'
              ? 'Om denna produkt:'
              : 'About this product:';

          let infoHtml = `
            <div class="custom-short-info-header">
              ${headerText}
            </div>
            <div class="short-info-content">
              ${shortInfo}
            </div>
          `;

          this.shortInfoContainer.innerHTML = infoHtml;
          this.shortInfoContainer.style.display = 'block';
        } else {
          this.shortInfoContainer.innerHTML = '';
          this.shortInfoContainer.style.display = 'none';
        }
      }
    }
  );
}
